import {faExternalLink} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {Alert, Checkbox, Flex, Tooltip} from 'antd';
import {memo, useCallback, useEffect, useRef, useState} from 'react';

import {Locale} from '../../../../../../provider/locale/locale';
import {formatAddress} from '../../../../../../service/address/address-helper';
import {useReviewRealtimePresence} from '../../../../../../service/reviews/review-realtime-presence/review-realtime-presence-context';
import {ReviewPresenceBlockedActionEnum} from '../../../../../../service/reviews/review-realtime-presence/review-realtime-presence-type';
import {useReviewsCatalogList} from '../../../../../../service/reviews/reviews-catalogs';
import {useReviewLogsList} from '../../../../../../service/reviews/reviews-logs';
import {useGetRequiredGroups} from '../../../../../../service/reviews/reviews-tags';
import {classNames} from '../../../../../../util/css';
import {useReviewActivityHook} from '../../../review-activity-provider/review-activity-provider';
import {NewReviewAnswers} from '../../../review-answers/new-review-answers';
import {ReviewCatalogLogo} from '../../../review-catalog-logo/review-catalog-logo';
import {ReviewPresenceBlockedTooltip} from '../../../review-realtime-presence/review-presence-blocked-tooltip/review-presence-blocked-tooltip';
import {ReviewStatus} from '../../../review-status/review-status';
import {useReviewStateHook} from '../../../reviews-state/reviews-state-provider';
import {TagList} from '../../../tag-list/tag-list';
import {NewReviewNotes} from '../../new-review-notes/new-review-notes';
import {ReviewDataWithCatalogType} from '../review-list-type';

import {NewReviewComment} from './new-review-comment/new-review-comment';
import {ReviewAdditionalDataTags} from './review-additional-data/review-additional-data-tags';
import {ReviewHeader} from './review-header/review-header';
import {getMissingRequiredGroups} from './review-helper';
import * as styles from './new-review.scss';

type PropsType = {
    className?: string;
    reviewData: ReviewDataWithCatalogType;
    isSelected?: boolean;
    onToggleSelect?: () => void;
    isFirstReview: boolean;
};

// eslint-disable-next-line complexity, max-statements
export const NewReview = memo(function ReviewInner(props: PropsType): JSX.Element {
    const {className, reviewData, isSelected, onToggleSelect, isFirstReview} = props;

    const {refreshReview, reviewsInfo, suggestionMap, refreshSuggestions, updateReview} = useReviewStateHook();

    const suggestion = suggestionMap[reviewData.id];

    const info = reviewsInfo?.results.find((reviewInfo) => reviewInfo.id === reviewData.id) || null;

    const {
        id,
        catalog,
        company,
        deletedAt,
        responsibleUser,
        brand,
        comment,
        processStatus,
        tags,
        originUrl,
        catalogId,
        children,
    } = reviewData;

    const isReviewDeleted = Boolean(deletedAt);
    const hasAnswerContent = !isReviewDeleted || children.length > 0 || Boolean(suggestion);

    const {isHistoryVisible} = useReviewActivityHook();
    const {refetch: loadLogs} = useReviewLogsList(id, isHistoryVisible);
    const {isFetching: isCatalogLoading} = useReviewsCatalogList();
    const {data: requiredGroups} = useGetRequiredGroups();
    const tagGroups = tags?.flatMap((tag) => (tag.folderId ? [tag.folderId] : []));
    const uniqueRequiredGroup = getMissingRequiredGroups(requiredGroups || [], tagGroups || []);
    const isGptSuggestSupported = catalog?.gpt_suggest_supported;

    const {isLockedByOther} = useReviewRealtimePresence();
    const isPresenceLocked = isLockedByOther(id);

    const handleToggleSelect = useCallback(() => {
        if (!isSelected && isPresenceLocked) {
            return;
        }

        onToggleSelect?.();
    }, [isPresenceLocked, isSelected, onToggleSelect]);

    const notesCount = info?.notesCount || 0;

    const cardRef = useRef<HTMLDivElement>(null);
    const [isWideLayout, setIsWideLayout] = useState<boolean>(true);

    useEffect(() => {
        const element = cardRef.current;
        let observer: ResizeObserver | null = null;

        if (element) {
            observer = new ResizeObserver((entries) => {
                const width = entries[0]?.contentRect.width ?? 0;

                setIsWideLayout(width > 500);
            });
            observer.observe(element);
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
    }, []);

    const isTourEligibleInHeader = isFirstReview && isWideLayout;
    const isTourEligibleInBody = isFirstReview && !isWideLayout;

    return (
        <div
            className={classNames(styles.Review, className, {
                [styles.Review_selected]: isSelected,
            })}
            ref={cardRef}
        >
            <ReviewPresenceBlockedTooltip
                blockedAction={ReviewPresenceBlockedActionEnum.BulkSelect}
                isBlocked={isPresenceLocked && !isSelected}
            >
                <Checkbox
                    checked={isSelected}
                    className={styles.Review_checkbox}
                    disabled={isPresenceLocked && !isSelected}
                    onChange={handleToggleSelect}
                />
            </ReviewPresenceBlockedTooltip>
            <Flex vertical>
                <div className={styles.Review_generalWrapper}>
                    <ReviewHeader
                        info={info}
                        isFirstReview={isFirstReview}
                        isReviewDeleted={isReviewDeleted}
                        isTourEligible={isTourEligibleInHeader}
                        reviewData={reviewData}
                    />
                    <ReviewStatus
                        brandId={brand.id}
                        className={styles.Review_status}
                        isFirstReview={isFirstReview}
                        isPresenceLocked={isPresenceLocked}
                        isTourEligible={isTourEligibleInBody}
                        onChange={loadLogs}
                        processStatus={processStatus}
                        reviewId={id}
                        updateReview={updateReview}
                    />
                    {!company ? (
                        <Flex align="center" gap={4} wrap="wrap">
                            {catalog && (
                                <div className={styles.Review_catalogCompanyLine}>
                                    <div className={styles.Review_catalogLogo}>
                                        <ReviewCatalogLogo isCatalogLoading={isCatalogLoading} logoUrl={catalog.logo} />
                                    </div>
                                    {!deletedAt && originUrl ? (
                                        <a
                                            className={styles.Review_catalogSourceLink}
                                            href={originUrl}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            {catalog?.name || ''}
                                            <FontAwesomeIcon icon={faExternalLink} />
                                        </a>
                                    ) : (
                                        <Text lighter>{catalog?.name || ''}</Text>
                                    )}
                                </div>
                            )}
                            {catalog && <Text lighter>•</Text>}
                            <Text bolder lighter>
                                {brand?.name}
                            </Text>
                        </Flex>
                    ) : (
                        <Flex align="center" gap={4} wrap="wrap">
                            {catalog && (
                                <div className={styles.Review_catalogCompanyLine}>
                                    <div className={styles.Review_catalogLogo}>
                                        <ReviewCatalogLogo isCatalogLoading={isCatalogLoading} logoUrl={catalog.logo} />
                                    </div>
                                    {!deletedAt && originUrl ? (
                                        <a
                                            className={styles.Review_catalogSourceLink}
                                            href={originUrl}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            {catalog?.name || ''}
                                            <FontAwesomeIcon icon={faExternalLink} />
                                        </a>
                                    ) : (
                                        <Text lighter>{catalog?.name || ''}</Text>
                                    )}
                                </div>
                            )}
                            {catalog && <Text lighter>•</Text>}
                            <Text lighter>
                                {company?.name}, {formatAddress(company?.address || null)}
                                {company?.code && ','}
                            </Text>
                            {company?.code && (
                                <Tooltip
                                    placement="bottom"
                                    title={<Locale stringKey="REVIEWS__REVIEW__COMPANY_CODE__HINT" />}
                                >
                                    <Text lighter>{company.code}</Text>
                                </Tooltip>
                            )}
                        </Flex>
                    )}

                    <ReviewAdditionalDataTags additionalData={info?.additionalData} />
                    <NewReviewComment catalogId={catalogId} comment={comment} reviewId={id} />
                    <TagList
                        deletedAt={deletedAt}
                        isPresenceLocked={isPresenceLocked}
                        responsibleUser={responsibleUser}
                        reviewId={id}
                        tags={tags}
                        updateReview={updateReview}
                    />
                    {uniqueRequiredGroup && uniqueRequiredGroup?.length > 0 && (
                        <Alert
                            className={styles.Review_requiredTagGroupsAlert}
                            message={
                                <Text
                                    secondary
                                    small
                                    stringKey="REVIEW__REQUIRED_GROUP__ALERT"
                                    valueMap={{
                                        tagsGroup: uniqueRequiredGroup?.map((group, index, array) => (
                                            <Text bolder key={group.id} small>
                                                {group.title}
                                                {index < array.length - 1 && ', '}
                                            </Text>
                                        )),
                                    }}
                                />
                            }
                            showIcon
                            type="warning"
                        />
                    )}

                    {notesCount > 0 && <NewReviewNotes notesCount={notesCount} reviewId={id} />}
                </div>
                {hasAnswerContent && (
                    <div className={styles.Review_answerWrapper}>
                        <NewReviewAnswers
                            isCollapsedAnswers
                            isGptSuggestSupported={isGptSuggestSupported}
                            refreshReview={refreshReview}
                            refreshSuggestions={refreshSuggestions}
                            review={reviewData}
                            suggestion={suggestion}
                        />
                    </div>
                )}
            </Flex>
        </div>
    );
});
